import { NavLink, useNavigate } from "react-router-dom";
import { Ticket as TicketIcon, ListChecks, UserPlus, PaperPlaneTilt, SignOut } from "@phosphor-icons/react";
import { Toaster } from "sonner";
import { initials } from "@/lib/format";

/**
 * PortalShell — left-sidebar layout for the requester portal. Mirrors
 * the staff `AppShell` structure but simplified to the three sections a
 * portal user has: their tickets, their form submissions, and (for
 * anyone who has submitted an onboarding form) their new starts
 * calendar. All views are scoped to the logged-in portal email.
 */

const Item = ({ to, icon: Icon, label, testId }) => (
  <NavLink
    to={to}
    end
    data-testid={testId}
    className={({ isActive }) =>
      `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors duration-100 ${
        isActive
          ? "bg-[#4f4c90]/10 text-[#4f4c90] font-medium"
          : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
      }`
    }
  >
    <Icon size={18} weight="duotone" />
    <span>{label}</span>
  </NavLink>
);

export default function PortalShell({ children }) {
  const navigate = useNavigate();
  const email = localStorage.getItem("hd_portal_email") || "";

  const logout = () => {
    localStorage.removeItem("hd_portal_token");
    localStorage.removeItem("hd_portal_email");
    navigate("/portal");
  };

  return (
    <div className="grain min-h-screen flex text-slate-900" style={{ background: "#FFFFFF" }}>
      <aside className="w-[240px] shrink-0 border-r border-slate-200 flex flex-col relative z-10 sticky top-0 self-start h-screen">
        <div className="px-4 py-4 flex items-center gap-2 border-b border-slate-200 shrink-0">
          <div className="w-8 h-8 rounded-md bg-[#4f4c90] flex items-center justify-center font-heading font-bold text-white">
            A
          </div>
          <div>
            <div className="font-heading font-bold text-[15px] leading-none text-[#263E4F]">Support portal</div>
            <div className="text-[11px] text-slate-500 font-mono mt-1">Aurora Energy Services</div>
          </div>
        </div>

        <nav className="p-2 flex flex-col gap-0.5">
          <Item to="/portal/submit" icon={PaperPlaneTilt} label="Submit a form" testId="portal-nav-submit" />
          <Item to="/portal/tickets" icon={TicketIcon} label="My tickets" testId="portal-nav-tickets" />
          <Item to="/portal/submissions" icon={ListChecks} label="Form submissions" testId="portal-nav-submissions" />
          <Item to="/portal/new-starts" icon={UserPlus} label="New starts" testId="portal-nav-new-starts" />
        </nav>

        <div className="mt-auto p-3 border-t border-slate-200">
          <div
            data-testid="portal-user-menu"
            className="flex items-center gap-2 p-2 rounded-md"
          >
            <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-medium">
              {initials(email)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm truncate" title={email}>{email.split("@")[0] || "You"}</div>
              <div className="text-[11px] text-slate-500 truncate" title={email}>{email}</div>
            </div>
            <button
              onClick={logout}
              data-testid="portal-logout-button"
              className="p-1.5 rounded hover:bg-slate-200 text-slate-600 hover:text-slate-900"
              title="Sign out"
            >
              <SignOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 min-w-0 relative z-10">
        <div className="p-6 fade-in">{children}</div>
      </main>
      <Toaster theme="light" position="bottom-right" />
    </div>
  );
}
